import { Modal } from "@seagames/ui";
import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * The Statuses guide (her V5 rule, admin.js `legendSet` / `legendBtn` /
 * `legendSheet`): every page that shows statuses has a "Statuses" button —
 * outlined, an info glyph, icon-only on narrow screens, in the toolbar after
 * the sort — opening a sheet "<title> · What each status and marker means".
 *
 * Build `groups` from the SAME status lists and chip / dot components the
 * page draws (pass the page's own `<…Dot>` as `marker`), so a renamed or new
 * status shows up here by itself. The component is translation-free apart
 * from its own chrome (`common:statusGuide.*`): every heading, label and
 * description arrives already translated.
 *
 *   <StatusGuide
 *     title={t("guide.title")}                       // "Room and stay statuses"
 *     groups={[
 *       { key: "people", heading: t("guide.people"), items: ROOMING_PERSON_STATES.map((s) => ({
 *           key: s, label: t(`person.${s}`), description: t(`person.hint.${s}`), marker: <PersonStateDot state={s} /> })) },
 *       { key: "tags", heading: t("guide.tags"), note: t("guide.tagsNote"), items: [
 *           { key: "late", icon: <ClockIcon />, tone: "warn", label: t("flag.late"), description: t("flag.lateHint") } ] },
 *     ]}
 *   />
 *
 * Placement: in a `ListToolbar`, pass it as `end` (after the sort); in a
 * section heading use `size="sm"` (her `.lg__h .rt__leg`, 32px).
 * `StatusGuideButton` + `StatusGuideSheet` are exported for a page that
 * wants to own the open state.
 */

export type StatusGuideTone = "ok" | "warn" | "bad" | "navy" | "mute";

export interface StatusGuideItem {
	/** Stable key (the status code). */
	key: string;
	/** Already translated: "Checked in". */
	label: ReactNode;
	/** Already translated, one line: "Checked in at the front desk". */
	description?: ReactNode;
	/**
	 * What the page draws for it, as is — a dot, a chip, a room swatch. Wins
	 * over `color` and `icon`.
	 */
	marker?: ReactNode;
	/** A plain round swatch in this CSS colour (her `fill`). */
	color?: string;
	/** A glyph in a small tinted square (her `sleg__ic`), toned by `tone` (default "mute"). */
	icon?: ReactNode;
	tone?: StatusGuideTone;
}

export interface StatusGuideGroup {
	key: string;
	/** Small uppercase heading ("People", "Rooms"). Left out when the guide has a single group without one. */
	heading?: ReactNode;
	/** One line under the heading. */
	note?: ReactNode;
	items: readonly StatusGuideItem[];
}

export interface StatusGuideProps {
	/** The sheet title, already translated ("Room and stay statuses"). */
	title: ReactNode;
	groups: readonly StatusGuideGroup[];
	/** "toolbar" = 2.875rem, beside a search box (default); "sm" = 2rem, in a section heading. */
	size?: "toolbar" | "sm";
	className?: string;
}

/** Button + sheet with its own open state — the usual way in. */
export function StatusGuide({ title, groups, size = "toolbar", className }: StatusGuideProps) {
	const [open, setOpen] = useState(false);
	return (
		<>
			<StatusGuideButton size={size} className={className} onClick={() => setOpen(true)} />
			<StatusGuideSheet open={open} onClose={() => setOpen(false)} title={title} groups={groups} />
		</>
	);
}

/** Her `.rt__leg`: outlined, info glyph + "Statuses"; the word hides under 45rem (44px icon button). */
export function StatusGuideButton({ onClick, size = "toolbar", className = "" }: { onClick: () => void; size?: "toolbar" | "sm"; className?: string }) {
	const { t } = useTranslation("common");
	const box =
		size === "sm"
			? "h-[2rem] gap-[0.375rem] px-[0.625rem] text-[0.78125rem] [&>svg]:size-[0.9375rem]"
			: "h-[2.875rem] gap-[0.375rem] px-[0.75rem] text-[0.875rem] max-[45rem]:w-[2.875rem] max-[45rem]:justify-center max-[45rem]:px-0 [&>svg]:size-[1rem]";
	return (
		<button
			type="button"
			onClick={onClick}
			title={t("statusGuide.subtitle")}
			aria-label={t("statusGuide.button")}
			aria-haspopup="dialog"
			className={`inline-flex shrink-0 cursor-pointer items-center rounded-[0.5rem] border border-gms-line-2 bg-base-100 font-semibold whitespace-nowrap text-gms-navy transition-colors outline-none hover:border-gms-navy focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${box} ${className}`}
		>
			<InfoGlyph />
			<span className={size === "sm" ? "" : "max-[45rem]:sr-only"}>{t("statusGuide.button")}</span>
		</button>
	);
}

const TONE: Record<StatusGuideTone, string> = {
	ok: "bg-gms-ok-soft text-gms-ok",
	warn: "bg-gms-warn-soft text-gms-warn",
	bad: "bg-gms-bad-soft text-gms-bad",
	navy: "bg-gms-navy-soft text-gms-navy",
	mute: "bg-gms-soft-2 text-gms-ink-3",
};

/** Her `legendSheet`: per group an uppercase heading, an optional note, then a bordered list of marker · label · description. */
export function StatusGuideSheet({
	open,
	onClose,
	title,
	groups,
}: {
	open: boolean;
	onClose: () => void;
	title: ReactNode;
	groups: readonly StatusGuideGroup[];
}) {
	const { t } = useTranslation("common");
	const showHeadings = groups.length > 1 || groups.some((g) => g.heading);
	return (
		<Modal open={open} onClose={onClose} size="md" title={title} description={t("statusGuide.subtitle")}>
			<div className="grid gap-[1.125rem]">
				{groups.map((g) => (
					<GuideGroup key={g.key} group={g} showHeading={showHeadings} />
				))}
			</div>
		</Modal>
	);
}

function GuideGroup({ group, showHeading }: { group: StatusGuideGroup; showHeading: boolean }) {
	const id = useId();
	return (
		<section aria-labelledby={showHeading && group.heading ? id : undefined} className="grid gap-[0.375rem]">
			{showHeading && group.heading && (
				<h3 id={id} className="text-[0.75rem] font-bold tracking-[0.04em] text-gms-ink-3 uppercase">
					{group.heading}
				</h3>
			)}
			{group.note && <p className="mb-[0.125rem] text-[0.78125rem] leading-snug text-gms-ink-3">{group.note}</p>}
			<ul className="grid overflow-hidden rounded-[0.625rem] border border-gms-line">
				{group.items.map((i) => (
					<li key={i.key} className="flex items-start gap-[0.75rem] border-t border-gms-line px-[0.75rem] py-[0.625rem] first:border-t-0">
						<span className="flex min-w-[1.375rem] shrink-0 justify-center pt-[0.125rem]">
							{i.marker ??
								(i.icon ? (
									<span className={`grid size-[1.375rem] place-items-center rounded-[0.25rem] [&>svg]:size-[0.875rem] ${TONE[i.tone ?? "mute"]}`}>
										{i.icon}
									</span>
								) : (
									<i
										aria-hidden
										className="mt-[0.125rem] block size-[0.75rem] rounded-full shadow-[inset_0_0_0_1px_rgba(18,20,63,0.12)]"
										style={{ background: i.color ?? "transparent" }}
									/>
								))}
						</span>
						<span className="grid min-w-0 gap-[0.0625rem]">
							<b className="text-[0.875rem] font-semibold text-gms-navy-ink">{i.label}</b>
							{i.description && <span className="text-[0.8125rem] leading-snug text-gms-ink-2">{i.description}</span>}
						</span>
					</li>
				))}
			</ul>
		</section>
	);
}

function InfoGlyph() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable={false}>
			<circle cx="12" cy="12" r="8.5" />
			<path d="M12 11v5M12 7.6v.2" />
		</svg>
	);
}
